import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const UrlInputSection = ({ 
  url, 
  setUrl, 
  onNotarize, 
  isLoading, 
  isValidUrl, 
  setIsValidUrl 
}) => {
  const [urlError, setUrlError] = useState('');

  const validateUrl = (inputUrl) => {
    if (!inputUrl) {
      setUrlError('');
      setIsValidUrl(false);
      return false;
    }

    try {
      const urlObj = new URL(inputUrl);
      if (!['http:', 'https:']?.includes(urlObj?.protocol)) {
        setUrlError('URL must use HTTP or HTTPS protocol');
        setIsValidUrl(false);
        return false;
      }
      
      setUrlError('');
      setIsValidUrl(true);
      return true;
    } catch (error) {
      setUrlError('Please enter a valid URL (e.g., https://example.com)');
      setIsValidUrl(false);
      return false;
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateUrl(url);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [url]);

  const handleUrlChange = (e) => {
    setUrl(e?.target?.value);
  };

  const handleNotarize = () => {
    if (isValidUrl && url) {
      onNotarize();
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
            <Icon name="Globe" size={32} className="text-primary" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Notarize Webpage
          </h1>
          <p className="text-muted-foreground">
            Create a permanent, timestamped archive of any webpage with blockchain verification
          </p>
        </div>

        {/* URL Input */}
        <div className="space-y-4">
          <Input
            label="Webpage URL"
            type="url"
            placeholder="https://example.com"
            value={url}
            onChange={handleUrlChange}
            error={urlError}
            description="Enter the complete URL of the webpage you want to notarize"
            required
            className="text-base"
          />

          {/* URL Preview */}
          {isValidUrl && url && (
            <div className="flex items-center space-x-2 p-3 bg-success/5 border border-success/20 rounded-md">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-success">Valid URL detected</p>
                <p className="text-xs text-success/80 truncate">{url}</p>
              </div>
            </div>
          )}

          {/* Action Button */}
          <Button
            variant="default"
            size="lg"
            fullWidth
            onClick={handleNotarize}
            disabled={!isValidUrl || !url || isLoading}
            loading={isLoading}
            iconName="Shield"
            iconPosition="left"
            className="mt-6"
          >
            {isLoading ? 'Processing...' : 'Start Notarization'}
          </Button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
          <div className="text-center">
            <Icon name="Camera" size={20} className="text-primary mx-auto mb-2" />
            <h3 className="text-sm font-medium text-foreground">Screenshot</h3>
            <p className="text-xs text-muted-foreground">Capture webpage content</p>
          </div>
          <div className="text-center">
            <Icon name="Database" size={20} className="text-primary mx-auto mb-2" />
            <h3 className="text-sm font-medium text-foreground">IPFS Storage</h3>
            <p className="text-xs text-muted-foreground">Decentralized storage</p>
          </div>
          <div className="text-center">
            <Icon name="Shield" size={20} className="text-primary mx-auto mb-2" />
            <h3 className="text-sm font-medium text-foreground">Blockchain</h3>
            <p className="text-xs text-muted-foreground">Immutable verification</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrlInputSection;